import React, { lazy, Suspense, useEffect } from 'react';
import { Route, Switch } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

import routeCodes from 'common/constants/route-codes';
import LazyLoader from 'components/atoms/Loaders/LazyLoader';
// import { useBootstrapCalls } from 'hooks/use-bootsrap-calls';
import { StyledToastContainer } from 'components/organisms/Toast';
import 'react-toastify/dist/ReactToastify.min.css';

import ProtectedRoute from './ProtectedRoute';
import { useUser } from '../../redux/user/use-user';
import SuperAdminRoutes from './SuperAdminRoutes';

import SettingsV2Page from 'pages/SettingsV2';
import SettingsV2IpraticoPage from 'pages/SettingsV2/ipratico';
import SettingsV2Delivery from 'pages/SettingsV2/delivery';
import SettingsV2ServiceCreate from 'pages/SettingsV2/service-create';
import SettingsV2ServiceEdit from 'pages/SettingsV2/service-edit';
import Notifications from '../organisms/Notifications';
import { useCommon } from 'redux/common/use-common';

const Home = lazy(() => import('pages/Home'));

const SignInPage = lazy(() => import('pages/auth/sign-in'));
const ForgetPassword = lazy(() => import('pages/auth/forget-password'));
const NewPasswordPage = lazy(() => import('pages/auth/new-password'));
const RegistrationPage = lazy(() => import('pages/auth/register'));

const RestaurantsList = lazy(() => import('pages/restaurant/list'));
const RestaurantsShow = lazy(() => import('pages/restaurant/show'));
const RestaurantsCreate = lazy(() => import('pages/restaurant/create'));

const ReminderPage = lazy(() => import('pages/Reminder'));
const ReservationsPage = lazy(() => import('pages/Reservations'));

const ProfileList = lazy(() => import('pages/profile/list'));
const ProfileShow = lazy(() => import('pages/profile/show'));

const Ipratico = lazy(() => import('pages/ipratico'));

const FirmList = lazy(() => import('pages/firm/list'));
const FirmShow = lazy(() => import('pages/firm/show'));
const FormCreate = lazy(() => import('pages/firm/create'));

const MenuPage = lazy(() => import('pages/menu'));
const MenuOptionsCreate = lazy(() => import('pages/menu/options/create'));
const MenuOptionsEdit = lazy(() => import('pages/menu/options/edit'));

const MenuCategories = lazy(() => import('pages/menu/categories'));
const MenuCategoriesCreate = lazy(() => import('pages/menu/categories/create'));
const MenuCategoriesEdit = lazy(() => import('pages/menu/categories/edit'));

const MenuDishesCreate = lazy(() => import('pages/menu/dishes/create'));
const MenuDishesEdit = lazy(() => import('pages/menu/dishes/edit'));

const MenuSpecialMenuCreate = lazy(() =>
  import('pages/menu/special_menu/create'),
);
const MenuSpecialMenuEdit = lazy(() => import('pages/menu/special_menu/edit'));

const ChooseRestaurant = lazy(() => import('pages/auth/choose-restaurant'));

const StatisticsPage = lazy(() => import('pages/Orders'));

const TeamPage = lazy(() => import('pages/Team'));
const TeamCreatePage = lazy(() => import('pages/Team/create'));
const TeamEditPage = lazy(() => import('pages/Team/edit'));

const FinancesPage = lazy(() => import('pages/Fincances'));

const HelpPage = lazy(() => import('pages/Help'));
const HelpMessagesPage = lazy(() => import('pages/Help/messages'));

const OffersMainPage = lazy(() => import('pages/Offers'));
const QRMainPage = lazy(() => import('pages/QR'));
const OffersCreateCalendars = lazy(() =>
  import('pages/Offers/create/bonus_calendars'),
);
const OffersCreatePayment = lazy(() =>
  import('pages/Offers/create/bonuse_payment'),
);
const OffersCreateAmount = lazy(() =>
  import('pages/Offers/create/bonus_amount'),
);
const OffersCreateBonusReservation = lazy(() =>
  import('pages/Offers/create/bonus_reservations'),
);

const AllBonuses = lazy(() => import('pages/all-bonuses'));

const RegisterPage = lazy(() => import('pages/register'));

const StripeRefresh = lazy(() => import('pages/Stripe/refresh'));
const StripeSuccess = lazy(() => import('pages/Stripe/success'));

const Routes = () => {
  const { getAuthUser, isSuperAdmin, getInProcess, userData } = useUser();
  const location = useLocation();

  useEffect(() => {
    const publicRoutes = [
      routeCodes.signIn,
      routeCodes.forgetPassword,
      routeCodes.newPassword,
      routeCodes.register
    ];
    
    const isPublicRoute = publicRoutes.includes(location.pathname);
    
    if (!isPublicRoute) {
      getAuthUser(location.pathname);
    }
  }, [location.pathname]);

  if (getInProcess) {
    return <LazyLoader />;
  }

  if (isSuperAdmin) {
    return <SuperAdminRoutes />;
  }

  return (
    <>
      <Notifications />
      <StyledToastContainer />
      
      <Suspense fallback={<LazyLoader />}>
        <Switch>
          <Route component={SignInPage} exact path={routeCodes.signIn} />
          <Route
            component={ForgetPassword}
            exact
            path={routeCodes.forgetPassword}
          />
          <Route 
            component={NewPasswordPage} 
            exact 
            path={routeCodes.newPassword}
          />
          <Route component={RegistrationPage} exact path={routeCodes.register} />

          <ProtectedRoute
            component={ChooseRestaurant}
            exact
            path={routeCodes.chooseRestaurant}
          />

          <ProtectedRoute component={Home} exact path={routeCodes.home} />

          <ProtectedRoute
            component={Ipratico}
            exact
            path={routeCodes.ipratico}
          />

          <ProtectedRoute
            component={RestaurantsList}
            exact
            path={routeCodes.restaurant}
          />
          <ProtectedRoute
            component={RestaurantsCreate}
            exact
            path={routeCodes.restaurantCreate}
          />
          <ProtectedRoute
            component={RestaurantsShow}
            exact
            path={routeCodes.restaurantView}
          />

          <ProtectedRoute
            component={AllBonuses}
            exact
            path={routeCodes.allBonuses}
          />

          <ProtectedRoute
            component={ProfileList}
            exact
            path={routeCodes.profile}
          />
          <ProtectedRoute
            component={ProfileShow}
            exact
            path={routeCodes.profileEdit}
          />

          <ProtectedRoute component={FirmList} exact path={routeCodes.firm} />
          <ProtectedRoute
            component={FormCreate}
            exact
            path={routeCodes.firmCreate}
          />
          <ProtectedRoute
            component={FirmShow}
            exact
            path={routeCodes.firmView}
          />

          {/* Menu pages start */}
          <ProtectedRoute component={MenuPage} exact path={routeCodes.menu} />
          <ProtectedRoute
            component={MenuCategories}
            exact
            path={routeCodes.menuCategories}
          />
          <ProtectedRoute
            component={MenuDishesCreate}
            exact
            path={routeCodes.menuDishesCreate}
          />
          <ProtectedRoute
            component={MenuDishesEdit}
            exact
            path={routeCodes.menuDishesEdit}
          />
          <ProtectedRoute
            component={MenuCategoriesCreate}
            exact
            path={routeCodes.menuCategoriesCreate}
          />
          <ProtectedRoute
            component={MenuCategoriesEdit}
            exact
            path={routeCodes.menuCategoriesEdit}
          />
          <ProtectedRoute
            component={MenuSpecialMenuCreate}
            exact
            path={routeCodes.specialMenuCreate}
          />
          <ProtectedRoute
            component={MenuSpecialMenuEdit}
            exact
            path={routeCodes.specialMenuEdit}
          />
          <ProtectedRoute
            component={MenuOptionsCreate}
            exact
            path={routeCodes.menuOptionsCreate}
          />
          <ProtectedRoute
            component={MenuOptionsEdit}
            exact
            path={routeCodes.menuOptionsEdit}
          />
          {/* Menu pages end */}

          <ProtectedRoute
            component={SettingsV2Page}
            exact
            path={routeCodes.settings}
          />
          <ProtectedRoute
            component={SettingsV2IpraticoPage}
            exact
            path={routeCodes.settingsIpratico}
          />
          <ProtectedRoute
            primary={false}
            component={SettingsV2Delivery}
            exact
            path={routeCodes.settingsConfigsDelivery}
          />
          <ProtectedRoute
            component={SettingsV2ServiceCreate}
            exact
            path={routeCodes.settingsConfigsServicesCreate}
          />
          <ProtectedRoute
            component={SettingsV2ServiceEdit}
            exact
            path={routeCodes.settingsConfigsServicesView}
          />

          {/*<ProtectedRoute component={SettingsPage} exact path={routeCodes.settings} />*/}
          {/*<ProtectedRoute component={SettingsConfigPage} exact path={routeCodes.settingsConfigs} />*/}
          {/*<ProtectedRoute component={SettingsConfigDelivery} exact path={routeCodes.settingsConfigsDelivery} />*/}
          {/*<ProtectedRoute component={SettingsTimePage} exact path={routeCodes.settingsTimes} />*/}
          {/*<ProtectedRoute component={SettingsConfigServiceCreate} exact path={routeCodes.settingsConfigsServicesCreate} />*/}
          {/*<ProtectedRoute component={SettingsConfigServiceEdit} exact path={routeCodes.settingsConfigsServicesView} />*/}
          {/*<ProtectedRoute component={MenuVariants} exact path={routeCodes.menuVariants} />*/}

          <ProtectedRoute
            component={StatisticsPage}
            exact
            path={routeCodes.statistics}
          />

          <ProtectedRoute
            component={ReminderPage}
            exact
            path={routeCodes.reminder}
          />
          <ProtectedRoute
            component={ReservationsPage}
            exact
            path={routeCodes.reservations}
          />

          <ProtectedRoute component={TeamPage} exact path={routeCodes.team} />
          <ProtectedRoute
            component={TeamCreatePage}
            exact
            path={routeCodes.teamCreate}
          />
          <ProtectedRoute
            component={TeamEditPage}
            exact
            path={routeCodes.teamShow}
          />

          <ProtectedRoute
            component={FinancesPage}
            exact
            path={routeCodes.finances}
          />

          <ProtectedRoute component={HelpPage} exact path={routeCodes.help} />
          <ProtectedRoute
            component={HelpMessagesPage}
            exact
            path={routeCodes.helpMessages}
          />

          <ProtectedRoute
            component={OffersMainPage}
            exact
            path={routeCodes.offers}
          />
          <ProtectedRoute
            component={OffersCreateCalendars}
            exact
            path={routeCodes.offersCreatCalendar}
          />
          <ProtectedRoute
            component={OffersCreatePayment}
            exact
            path={routeCodes.offersCreatPayment}
          />
          <ProtectedRoute
            component={OffersCreateAmount}
            exact
            path={routeCodes.offersCreatBonusAmount}
          />
          <ProtectedRoute
            component={OffersCreateBonusReservation}
            exact
            path={routeCodes.offersCreatBonusReservation}
          />

          <ProtectedRoute
            component={RegisterPage}
            exact
            path={routeCodes.register}
          />

          <ProtectedRoute
            component={StripeRefresh}
            exact
            path={routeCodes.stripeRefresh}
          />
          <ProtectedRoute
            component={StripeSuccess}
            exact
            path={routeCodes.stripeSuccess}
          />
          <ProtectedRoute component={QRMainPage} exact path={routeCodes.qr} />
          <Route component={() => <div>404</div>} />
        </Switch>
      </Suspense>
    </>
  );
};

export default Routes;
